/**
 * Single selectable row in the default paths list (Voluum-style).
 * Row chrome aligned with FlowPathsPathRow on the campaign flow page.
 */

import type { FC } from 'react';
import type { VoluumPathUpdate } from '@trafficpulse/shared';

import { Switch } from '@/components/ui/form/controls/Switch';
import { cn } from '@/lib/utils';
import {
  usePreviewMode,
  usePreviewRow,
} from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import PreviewAwareWeightSlot from '@/features/flow-optimizer/components/preview/PreviewAwareWeightSlot';
import PreviewExcludedIndicator from '@/features/flow-optimizer/components/preview/PreviewExcludedIndicator';
import { FlowTagChips } from '@/features/flow-optimizer/components/preview/FlowTagChips';
import { StandardOfferCapChips } from '@/features/flow-optimizer/tabs/standard/v2/caps/StandardOfferCapChips';
import { excludedReasonText, showExcludedBadge } from '@/features/flow-optimizer/components/preview/preview-row.types';
import { usePathDirtyStatus } from '@/features/flow-optimizer/standard-draft';

import DefaultPathDirtyBadge from './DefaultPathDirtyBadge';

export interface DefaultPathListRowProps {
  path: VoluumPathUpdate;
  selected: boolean;
  weightPercent: number;
  weightLocked: boolean;
  onSelect: () => void;
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void;
}

const DefaultPathListRow: FC<DefaultPathListRowProps> = ({
  path,
  selected,
  weightPercent,
  weightLocked,
  onSelect,
  onPatch,
}) => {
  const previewMode = usePreviewMode();
  const previewRow = usePreviewRow('paths', path.id ?? '');
  const previewLocked =
    previewMode && previewRow != null && !showExcludedBadge(previewRow, Boolean(path.active));
  const gridCols = previewLocked
    ? 'grid-cols-[minmax(0,1fr)_3rem_0.75rem_3rem_3.25rem_2.75rem]'
    : 'grid-cols-[minmax(0,1fr)_3.5rem_5rem_2.75rem]';

  const hoverName = path.name?.trim() ? path.name : `Path ${path.id ?? ''}`.trim();
  const dirtyStatus = usePathDirtyStatus(path.id);
  const dirty = dirtyStatus?.dirty ?? false;

  return (
      <div
        role="option"
        aria-selected={selected}
        className={cn(
          'transition-colors outline-none',
          selected && 'bg-primary/5',
          !path.active && 'opacity-70',
          dirty && 'border-l-2 border-warning bg-warning/5',
        )}
      >
      <div
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
          }
        }}
        className="w-full px-1.5 py-1 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
      >
        <div className={cn('grid w-full items-center gap-x-1.5 gap-y-0', gridCols)}>
          {/* The name keeps ≥5rem and takes the rest of the line; the chips stay
                whole and drop to a second line only when the row is too narrow. */}
            <span className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-0.5">
            <span className="min-w-0 flex-[1_1_5rem] truncate text-xs font-medium leading-tight text-foreground">
              {path.name?.trim() ? path.name : 'Untitled path'}
            </span>
            <FlowTagChips layer="paths" itemId={path.id ?? ''} className="shrink-0 gap-1" />
            <StandardOfferCapChips offerIds={(path.offers ?? []).map((o) => o.offer.id)} />
            {showExcludedBadge(previewRow, Boolean(path.active)) ? (
              <PreviewExcludedIndicator
                title={excludedReasonText(previewRow)}
                row={previewRow}
                name={hoverName}
              />
            ) : null}
            {dirtyStatus?.dirty ? (
              <DefaultPathDirtyBadge
                changedFields={dirtyStatus.fields}
                onRestore={dirtyStatus.restore}
              />
            ) : null}
          </span>
          <div
            className={cn('flex items-center justify-end', previewLocked && 'col-span-4')}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <PreviewAwareWeightSlot
              name={hoverName}
              previewRow={previewRow}
              previewMode={previewMode}
              active={Boolean(path.active)}
              value={path.weight}
              onChange={(v) =>
                onPatch((p) => {
                  p.weight = v;
                })
              }
              ariaLabel={`Weight for ${path.name || path.id}`}
              forceReadOnly={!previewMode && weightLocked}
              forceReadOnlyHint="Path weight is managed by Voluum (Traffic Distribution AI on for default paths)"
            />
          </div>
          {!previewLocked ? (
            <div
              className={cn(
                'flex min-h-[1.25rem] items-center justify-end text-right text-[11px] leading-none text-muted-foreground',
                path.active && 'tabular-nums',
              )}
              title={
                path.active
                  ? undefined
                  : 'Traffic share is not applied while this path is inactive'
              }
            >
              {path.active ? `${weightPercent.toFixed(2)}%` : 'N/A'}
            </div>
          ) : null}
          <div
            className="flex items-center justify-end"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Switch
              checked={path.active}
              onCheckedChange={(v) =>
                onPatch((p) => {
                  p.active = v;
                })
              }
              aria-label={`${path.active ? 'Disable' : 'Enable'} ${path.name || path.id}`}
            />
          </div>
        </div>
      </div>
      </div>
  );
};

export default DefaultPathListRow;
